import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import React, { useState, useEffect, useLayoutEffect } from 'react';
import '../../../../styles/_Chart_Hook_BasicLine.scss';
import { useTranslation } from 'react-i18next';
import { HR, RR } from './SVGRRHR';
import ConfigHandler from '../../../../actions/ConfigHandler';
import ApiGo from '../../../../actions/ApiGo';

const TAIPEI_TIMEZONE = 'Asia/Taipei';

const VitalChart = (props) => {
    const isHR = ConfigHandler.isShowCF();
    const { type, breath_high, breathData, timeInterval, yAxis, residentSetting } = props;
    const { t } = useTranslation();
    const [breath, setBreath] = useState();
    const a = useWindowSize();

    useEffect(() => {
        const fetchData = async () => {
            // timeInterval 直接視為 timestamp(ms)，不要再額外加 UTC+8
            const timeIntervalMin = Number(timeInterval[0]);
            const timeIntervalMax = Number(timeInterval[1]);

            // 若後端 created_at 是台灣時間字串（例如 2025-08-26 06:00:00），
            // 這裡轉成 UTC timestamp，讓圖表資料統一處理
            const _date_previous = formatTimestampToTaipeiString(timeIntervalMin);
            const _date = formatTimestampToTaipeiString(timeIntervalMax);

            let breathRowData = [];
            if (ConfigHandler.isFdaClass != 2) {
                try {
                    const res = await ApiGo.postHR_RR_abnormal_info(
                        residentSetting.id,
                        residentSetting.heart_high,
                        residentSetting.heart_low,
                        residentSetting.breath_high,
                        residentSetting.breath_low,
                        _date_previous,
                        _date
                    );

                    breathRowData = (res || []).map((r) => [
                        parseTaipeiDateTimeToMs(r.created_at),
                        r.value,
                    ]);
                } catch (error) {
                    console.error('Error fetching breath row data:', error);
                }
            }

            const adjustedAverageData = (breathData || []).map((res) => {
                return (res || [])
                    .map((row) => {
                        return [parseTaipeiDateTimeToMs(row.created_at), row.average];
                    })
                    .filter((row) => row[0] >= timeIntervalMin && row[0] <= timeIntervalMax);
            });

            const combinedData = [...adjustedAverageData.flat(), ...breathRowData]
                .filter((point) => Number.isFinite(point[0]) && Number.isFinite(point[1]))
                .sort((a, b) => a[0] - b[0])
                .map((dataPoint) => {
                    let value = dataPoint[1];
                    if (value < type.low) value = type.low;
                    if (value > type.high) value = type.high;
                    return [dataPoint[0], value];
                });

            const seriesData = {
                type: 'spline',
                name: t(type.avg),
                data: combinedData,
                color: '#29b5d6',
                zIndex: 1,
                marker: {
                    radius: 5,
                    states: {
                        hover: {
                            enabled: true,
                            lineColor: 'rgb(100,100,100)',
                        },
                    },
                },
                showInLegend: false,
            };

            const breathRow = {
                type: 'scatter',
                name: t('HISTORY_RECORD.Incident_Report'),
                data: breathRowData,
                color: 'red',
                zIndex: 2,
                marker: {
                    symbol: 'circle',
                    radius: 3,
                    states: {
                        hover: {
                            enabled: true,
                            lineColor: 'rgb(100,100,100)',
                        },
                    },
                },
            };

            setBreath({
                chart: {
                    height: isHR ? 250 : 450 * a[3],
                    backgroundColor: type.backgroundColor,
                    borderRadius: 20,
                },
                title: { text: t(type.title) },
                time: {
                    useUTC: true,
                },
                xAxis: {
                    type: 'datetime',
                    min: timeIntervalMin,
                    max: timeIntervalMax,
                    labels: {
                        formatter: function () {
                            return formatTaipeiHM(this.value);
                        },
                    },
                },
                exporting: { enabled: false },
                yAxis: {
                    title: { text: null },
                    min: 0,
                    max: yAxis.y,
                    plotLines: ConfigHandler.isFdaClass == 2 ? [] : [
                        {
                            color: '#735B69',
                            width: 2,
                            value: type.high,
                            label: {
                                style: {
                                    fontSize: '16px',
                                    backgroundColor: 'white',
                                },
                                align: 'right',
                                textAlign: 'right',
                                text: t('DASHBOARD.UPPER'),
                                verticalAlign: 'middle',
                                y: -10,
                            },
                        },
                        {
                            color: '#735B69',
                            width: 2,
                            value: type.low,
                            zIndex: 5,
                            label: {
                                style: {
                                    fontSize: '16px',
                                    backgroundColor: 'white',
                                },
                                align: 'right',
                                textAlign: 'right',
                                text: t('DASHBOARD.LOWER'),
                                verticalAlign: 'middle',
                                y: -10,
                            },
                        },
                    ],
                },
                tooltip: ConfigHandler.isFdaClass == 2 ? { enabled: false } : {
                    backgroundColor: 'white',
                    borderColor: '#FFE7C7',
                    shadow: false,
                    borderWidth: 3,
                    crosshairs: true,
                    shared: true,
                    valueSuffix: ` ${t('HISTORY_RECORD.TIMES_MIN')}`,
                    formatter: function () {
                        try {
                            const head = formatTaipeiDateTime(this.x);
                            const value = this.points
                                ? `${t('DASHBOARD.BREATH_TIMES')} : ${this.points[0].y}`
                                : `${t('HISTORY_RECORD.Incident_Report')} : ${this.y}`;

                            return `<div><div>${head}</div><br /><div>${value}</div></div>`;
                        } catch (err) {
                            console.error('Error formatting tooltip:', err);
                            return '';
                        }
                    },
                },
                legend: {
                    enabled: true,
                    layout: 'vertical',
                    align: 'right',
                    verticalAlign: 'top',
                    x: -150,
                    y: 0,
                    floating: true,
                },
                series: ConfigHandler.isFdaClass != 2 ? [seriesData, breathRow] : [seriesData],
            });
        };

        fetchData();
    }, [
        breathData,
        timeInterval,
        yAxis.y,
        t,
        isHR,
        a,
        type.avg,
        type.backgroundColor,
        type.title,
        breath_high,
        residentSetting,
        type.low,
        type.high,
    ]);

    return (
        <div className={`max-hig-vital fx-d-r br-20 ${type.type === 'RR' ? 'RR' : 'HR'} ${isHR ? 'HR cardDefaultCF' : ''}`}>
            {isHR ? (
                type.type === 'RR' ? (
                    <div className='w-5 h-100 fx-ccc'>
                        <RR type={'history'} />
                    </div>
                ) : (
                    <div className='w-5 h-100 fx-ccc'>
                        <HR type={'history'} />
                    </div>
                )
            ) : null}

            <div className={`${isHR ? 'w-95' : 'w-100'} h-100 bb ${type.type === 'RR' ? 'RR' : 'HR'}`}>
                <HighchartsReact highcharts={Highcharts} options={breath || {}} />
            </div>
        </div>
    );
};

export default VitalChart;

function useWindowSize() {
    const [size, setSize] = useState([
        window.innerWidth,
        window.innerHeight,
        window.innerWidth / 1920,
        window.innerHeight / 969,
    ]);

    useLayoutEffect(() => {
        function updateSize() {
            setSize([
                window.innerWidth,
                window.innerHeight,
                window.innerWidth / 1920,
                window.innerHeight / 969,
            ]);
        }

        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    return size;
}

// 把 "2025-08-26 06:00:00" 當作台灣時間解析成 UTC timestamp(ms)
function parseTaipeiDateTimeToMs(_str) {
    if (!_str) return NaN;

    const str = String(_str).trim();

    // 如果本來就帶時區，直接交給 Date 處理
    if (/[zZ]$/.test(str) || /[+-]\d{2}:\d{2}$/.test(str)) {
        return new Date(str.replace(' ', 'T')).getTime();
    }

    // 預設當成 Asia/Taipei (+08:00)
    return new Date(str.replace(' ', 'T') + '+08:00').getTime();
}

// 將 timestamp(ms) 格式化成台灣時間字串給 API 使用
function formatTimestampToTaipeiString(ms) {
    return new Intl.DateTimeFormat('sv-SE', {
        timeZone: TAIPEI_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).format(new Date(ms)).replace(' ', ' ');
}

// X 軸顯示 HH:mm，固定用台灣時間
function formatTaipeiHM(ms) {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: TAIPEI_TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(new Date(ms));

    const map = Object.fromEntries(parts.filter((p) => p.type !== 'literal').map((p) => [p.type, p.value]));
    return `${map.hour}:${map.minute}`;
}

// tooltip 顯示 YYYY-MM-DD HH:mm，固定用台灣時間
function formatTaipeiDateTime(ms) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: TAIPEI_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(new Date(ms));

    const map = Object.fromEntries(parts.filter((p) => p.type !== 'literal').map((p) => [p.type, p.value]));
    return `${map.year}-${map.month}-${map.day} ${map.hour}:${map.minute}`;
}